import React, { useMemo, useState } from 'react';
import {
  ACCESS_SETTING_LABELS,
  VIEW_ACCESS_LABELS,
  type AccessSetting,
  type CommunityChat,
  type CommunityForumState,
  type SectionStatus,
  type ViewAccessSetting,
} from './types';
import { SECTION_ICONS } from './communityUtils';
import { useCommunityForum } from './CommunityForumContext';
import type { ChatInput } from './communityForumStore';
import { CommunityDevRowMeta, CommunityDevTh } from './CommunityDevUi';
import { entityRef, P } from './communityDevMode';
import CommunityChannelAccessModal from './CommunityChannelAccessModal';

function defaultHeaderId(state: CommunityForumState): string {
  return state.headers.find((h) => h.status === 'active')?.id ?? state.headers[0]?.id ?? '';
}

function emptyChat(state: CommunityForumState, headerId?: string): ChatInput {
  const hdr = headerId ?? defaultHeaderId(state);
  const count = state.chats.filter((c) => c.headerId === hdr).length;
  return {
    headerId: hdr,
    name: '',
    description: '',
    icon: 'fa-comments',
    sortOrder: count + 1,
    status: 'active',
    postAccess: 'all',
    viewAccess: 'all',
  };
}

function chatToForm(chat: CommunityChat): ChatInput {
  return {
    headerId: chat.headerId,
    name: chat.name,
    description: chat.description,
    icon: chat.icon,
    sortOrder: chat.sortOrder,
    status: chat.status,
    postAccess: chat.postAccess,
    viewAccess: chat.viewAccess ?? 'all',
  };
}

export default function CommunityAdminChats() {
  const { state, addChat, editChat, removeChat, moveChats, getChannelAccessGrants } = useCommunityForum();
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ChatInput>(() => emptyChat(state));
  const [accessChat, setAccessChat] = useState<CommunityChat | null>(null);

  const headerTitle = (id: string) => state.headers.find((h) => h.id === id)?.title ?? '—';

  const chats = useMemo(() => {
    const list = [...state.chats].sort((a, b) => a.sortOrder - b.sortOrder);
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter(
      (c) => c.name.toLowerCase().includes(q) || c.description.toLowerCase().includes(q),
    );
  }, [state.chats, search]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyChat(state));
    setFormOpen(true);
  };

  const openEdit = (chat: CommunityChat) => {
    setEditingId(chat.id);
    setForm(chatToForm(chat));
    setFormOpen(true);
  };

  const saveForm = () => {
    if (!form.name.trim() || !form.headerId) return;
    if (editingId) editChat(editingId, form);
    else addChat(form);
    setFormOpen(false);
  };

  const moveUp = (id: string) => {
    const ordered = [...state.chats].sort((a, b) => a.sortOrder - b.sortOrder).map((c) => c.id);
    const idx = ordered.indexOf(id);
    if (idx <= 0) return;
    [ordered[idx - 1], ordered[idx]] = [ordered[idx], ordered[idx - 1]];
    moveChats(ordered);
  };

  const moveDown = (id: string) => {
    const ordered = [...state.chats].sort((a, b) => a.sortOrder - b.sortOrder).map((c) => c.id);
    const idx = ordered.indexOf(id);
    if (idx < 0 || idx >= ordered.length - 1) return;
    [ordered[idx], ordered[idx + 1]] = [ordered[idx + 1], ordered[idx]];
    moveChats(ordered);
  };

  const setField = <K extends keyof ChatInput>(key: K, value: ChatInput[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  if (state.headers.length === 0) {
    return (
      <p className="mt-6 text-sm text-slate-500">
        Сначала создайте хотя бы один заголовок на вкладке «Заголовки».
      </p>
    );
  }

  return (
    <div className="mt-6">
      <div className="dcf-toolbar">
        <button type="button" className="dcf-btn dcf-btn--primary" onClick={openCreate}>
          <i className="fas fa-plus" aria-hidden="true" />
          Создать чат
        </button>
        <input
          className="dcf-input dcf-search"
          placeholder="Поиск по чатам"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="dcf-admin-table-wrap mt-4">
        <table className="dcf-admin-table">
          <thead>
            <tr>
              <CommunityDevTh label="Название" storagePath={P.chatName} />
              <CommunityDevTh label="Заголовок" storagePath={P.chatHeaderId} />
              <th>Видимость</th>
              <th>Статус</th>
              <th>Сообщений</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {chats.map((chat) => (
              <tr key={chat.id}>
                <td>
                  <div className="font-semibold text-slate-800">{chat.name}</div>
                  <div className="text-xs text-slate-500 mt-1 max-w-xs">{chat.description}</div>
                  <CommunityDevRowMeta parts={[entityRef(P.chats, chat.id), P.chatDescription, P.chatPostAccess]} />
                </td>
                <td>{headerTitle(chat.headerId)}</td>
                <td>
                  <span className="dcf-status-pill dcf-status-pill--muted">
                    {VIEW_ACCESS_LABELS[chat.viewAccess ?? 'all']}
                  </span>
                  {(chat.viewAccess ?? 'all') === 'invite' && (
                    <div className="text-xs text-slate-500 mt-1">
                      {getChannelAccessGrants('chat', chat.id).length} с доступом
                    </div>
                  )}
                </td>
                <td>
                  <span className={`dcf-status-pill ${chat.status === 'active' ? 'dcf-status-pill--active' : 'dcf-status-pill--hidden'}`}>
                    {chat.status === 'active' ? 'Активен' : 'Скрыт'}
                  </span>
                </td>
                <td>{chat.messageCount ?? 0}</td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    <button type="button" className="dcf-btn dcf-btn--ghost text-xs" onClick={() => moveUp(chat.id)}>↑</button>
                    <button type="button" className="dcf-btn dcf-btn--ghost text-xs" onClick={() => moveDown(chat.id)}>↓</button>
                    <button type="button" className="dcf-btn dcf-btn--ghost text-xs" onClick={() => openEdit(chat)}>Редактировать</button>
                    {(chat.viewAccess ?? 'all') === 'invite' && (
                      <button
                        type="button"
                        className="dcf-btn dcf-btn--ghost text-xs"
                        onClick={() => setAccessChat(chat)}
                      >
                        Доступ
                      </button>
                    )}
                    <button
                      type="button"
                      className="dcf-btn dcf-btn--ghost text-xs"
                      onClick={() => editChat(chat.id, { status: chat.status === 'active' ? 'hidden' : 'active' })}
                    >
                      {chat.status === 'active' ? 'Скрыть' : 'Показать'}
                    </button>
                    <button
                      type="button"
                      className="dcf-btn dcf-btn--ghost dcf-btn--danger text-xs"
                      onClick={() => {
                        if (window.confirm(`Удалить чат «${chat.name}» и все сообщения?`)) {
                          removeChat(chat.id);
                        }
                      }}
                    >
                      Удалить
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {formOpen && (
        <div className="dcf-modal-overlay" onClick={() => setFormOpen(false)} role="presentation">
          <div className="dcf-modal dcf-modal--wide" onClick={(e) => e.stopPropagation()} role="dialog">
            <h2 className="dcf-modal__title">{editingId ? 'Редактировать чат' : 'Создать чат'}</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="dcf-field sm:col-span-2">
                <label className="dcf-field__label">Заголовок в сайдбаре</label>
                <select className="dcf-select" value={form.headerId} onChange={(e) => setField('headerId', e.target.value)}>
                  {state.headers.map((h) => (
                    <option key={h.id} value={h.id}>{h.title}</option>
                  ))}
                </select>
              </div>
              <div className="dcf-field sm:col-span-2">
                <label className="dcf-field__label">Название чата</label>
                <input className="dcf-input" value={form.name} onChange={(e) => setField('name', e.target.value)} />
              </div>
              <div className="dcf-field sm:col-span-2">
                <label className="dcf-field__label">Описание</label>
                <textarea className="dcf-textarea" rows={2} value={form.description} onChange={(e) => setField('description', e.target.value)} />
              </div>
              <div className="dcf-field">
                <label className="dcf-field__label">Иконка</label>
                <select className="dcf-select" value={form.icon} onChange={(e) => setField('icon', e.target.value)}>
                  {SECTION_ICONS.map((icon) => (
                    <option key={icon} value={icon}>{icon.replace('fa-', '')}</option>
                  ))}
                </select>
              </div>
              <div className="dcf-field">
                <label className="dcf-field__label">Порядок</label>
                <input className="dcf-input" type="number" value={form.sortOrder} onChange={(e) => setField('sortOrder', Number(e.target.value) || 1)} />
              </div>
              <div className="dcf-field">
                <label className="dcf-field__label">Статус</label>
                <select className="dcf-select" value={form.status} onChange={(e) => setField('status', e.target.value as SectionStatus)}>
                  <option value="active">Активен</option>
                  <option value="hidden">Скрыт</option>
                </select>
              </div>
              <div className="dcf-field">
                <label className="dcf-field__label">Видимость</label>
                <select className="dcf-select" value={form.viewAccess} onChange={(e) => setField('viewAccess', e.target.value as ViewAccessSetting)}>
                  {(Object.keys(VIEW_ACCESS_LABELS) as ViewAccessSetting[]).map((k) => (
                    <option key={k} value={k}>{VIEW_ACCESS_LABELS[k]}</option>
                  ))}
                </select>
              </div>
              <div className="dcf-field sm:col-span-2">
                <label className="dcf-field__label">Кто может писать</label>
                <select className="dcf-select" value={form.postAccess} onChange={(e) => setField('postAccess', e.target.value as AccessSetting)}>
                  {(Object.keys(ACCESS_SETTING_LABELS) as AccessSetting[]).map((k) => (
                    <option key={k} value={k}>{ACCESS_SETTING_LABELS[k]}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="dcf-modal__actions">
              <button type="button" className="dcf-btn dcf-btn--secondary" onClick={() => setFormOpen(false)}>Отмена</button>
              {editingId && form.viewAccess === 'invite' && (
                <button
                  type="button"
                  className="dcf-btn dcf-btn--secondary"
                  onClick={() => {
                    const chat = state.chats.find((c) => c.id === editingId);
                    if (chat) setAccessChat(chat);
                  }}
                >
                  Управление доступом
                </button>
              )}
              <button type="button" className="dcf-btn dcf-btn--primary" onClick={saveForm}>Сохранить</button>
            </div>
          </div>
        </div>
      )}

      {accessChat && (
        <CommunityChannelAccessModal
          open
          channelKind="chat"
          channelId={accessChat.id}
          channelName={accessChat.name}
          onClose={() => setAccessChat(null)}
        />
      )}
    </div>
  );
}
